import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Property from "./property";
import ImportProperties from "./ImportProperties";
import Logo from "./assets/logo.png";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import Users from "../pages/users";
import Team from "../pages/teammanager";
import { EventManagement } from "../pages/eventManagement";
import ManageBlog from "../pages/ManageBlog";
import CommunityManagePage from "../pages/communityManager";
import { FloorPlanManagement } from "../pages/FloorPlanManagement";
import { DocManagement } from "../pages/docManagement";
import ManageBoardMembers from "../pages/bordManagement";
import Setting from "../pages/setting";
import SubdivisionManagement from "../pages/subdivisionManagement";
import CompetingAgentManagement from "../pages/competingAgentManagement";
import { useSettings } from "../hooks/useSettings";

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSettings();

  // Get initial tab from URL or default to "Listings"
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      return decodeURIComponent(tabFromUrl);
    }
    return "Listings";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync activeTab with URL when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      const decodedTab = decodeURIComponent(tabFromUrl);
      setActiveTab(decodedTab);
    } else {
      // If no tab in URL, set default
      setActiveTab("Listings");
    }
  }, [searchParams]);

  // Update URL when activeTab changes
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    // Encode the tab name for URL (handles spaces and special characters)
    setSearchParams({ tab: encodeURIComponent(tabName) });
    setIsSidebarOpen(false);
  };

  const handleLogOut = async () => {
    logout();
    navigate("/");
  };
  const renderContent = () => {
    switch (activeTab) {
      // case "Dashboard":
      //   return <Dashboard />;
      case "Listings":
        return <Property />;
      case "CW Admins":
        return <Users />;
      case "CW Agents":
        return <Team />;
      case "Calendar":
        return <EventManagement />;
      case "News":
        return <ManageBlog />;
      case "Floor Plans":
        return <FloorPlanManagement />;
      case "HOA Documents":
        return <DocManagement />;
      case "Board Members":
        return <ManageBoardMembers />;
      case "Subdivisions":
        return <SubdivisionManagement />;
      case "Competing Agents":
        return <CompetingAgentManagement />;
      case "Settings":
        return <Setting />;
      case "Import Properties":
        return (
          <ImportProperties communityId={localStorage.getItem("communityId")} />
        );
      default:
        return <div>Select a tab</div>;
    }
  };

  // Function to handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="flex flex-col h-screen font-Poppins lg:flex-row">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      <aside
        className={`
          fixed 
          top-0 
          left-0 
          h-full 
          bg-red-600 
          text-white 
          shadow-md 
          w-52
          transform 
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          transition-transform duration-300 
          z-30 
          lg:static 
          lg:translate-x-0 
          lg:w-52
          flex
          flex-col
        `}
      >
        <div className="flex items-center justify-between py-4 px-3 flex-shrink-0">
          <div className="flex items-center">
            <img
              src={settings?.logoUrl || settings?.logoPath || Logo}
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-sm font-semibold hidden lg:inline-block ml-2">
              {settings?.clubName || "Admin"}
            </span>
          </div>
          <button
            className="lg:hidden text-white focus:outline-none"
            onClick={toggleSidebar}
            aria-label="Close Sidebar"
          >
            <Icon icon="mdi:close" className="text-xl" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-2">
          {[
            // { name: "Dashboard", icon: "material-symbols:dashboard-rounded" },
            { name: "Listings", icon: "mdi:home" },
            { name: "CW Admins", icon: "mdi:account-group" },
            { name: "CW Agents", icon: "mdi:account-group" },
            { name: "Subdivisions", icon: "mdi:map-marker-radius" },
            { name: "Competing Agents", icon: "mdi:account-tie" },
            { name: "News", icon: "mdi:blog" },
            { name: "Floor Plans", icon: "mdi:floor-plan" },
            { name: "HOA Documents", icon: "mdi:document" },
            { name: "Settings", icon: "mdi:settings" },
            { name: "Import Properties", icon: "mdi:file-import" },
          ].map(({ name, icon }) => (
            <button
              key={name}
              onClick={() => handleTabChange(name)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${activeTab === name
                  ? "bg-red-500 text-white"
                  : "hover:bg-red-700 hover:text-white"
                }`}
              aria-current={activeTab === name ? "page" : undefined}
            >
              <Icon icon={icon} className="text-lg" />
              <span className="ml-1">{name}</span>
            </button>
          ))}

          {[
            { name: "Board Members", icon: "mdi:account-group" },
            { name: "Calendar", icon: "mdi:events" },
          ].map(({ name, icon }) => (
            <button
              key={name}
              aria-disabled
              className={
                "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium opacity-50 cursor-not-allowed"
              }
            >
              <Icon icon={icon} className="text-lg" />
              <span className="ml-1">{name}</span>
            </button>
          ))}
        </nav>

        {/* Logout Button - Fixed at bottom */}
        <div className="flex-shrink-0 px-2 pb-3">
          <button
            onClick={handleLogOut}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md font-medium text-white hover:bg-red-700 hover:text-white transition"
          >
            <Icon icon="mdi:logout" className="text-lg" />
            <span className="ml-1">Logout</span>
          </button>
        </div>
      </aside>

      {/* Top Navigation Bar for Mobile */}
      <header className="flex items-center justify-between bg-red-600 text-white p-4 lg:hidden">
        <button
          onClick={toggleSidebar}
          className="focus:outline-none"
          aria-label="Open Sidebar"
        >
          <Icon icon="mdi:menu" className="text-2xl" />
        </button>
        <div className="flex items-center">
          <img src={Logo} className="w-8 h-8 object-contain" alt="Logo" />
          <span className="ml-2 text-lg font-semibold">{settings?.clubName || "Admin"}</span>
        </div>
        {/* Placeholder for alignment */}
        <div className="w-8 h-8"></div>
      </header>

      {/* Main Content */}
      <main className="flex-1 bg-gray-100 h-screen overflow-y-auto">
        {renderContent()}
      </main>
    </div>
  );
};

export default Admin;
